/* global Office */

import { addinOrigin, apiBaseUrl } from "../auth/authConfig";
import { authHeaders, getToken } from "../auth/authDialog";
import { BankInfo, MarketOption, marketFormulaCode } from "./components/types";

// Basemap tiles come from the API (GET /map/config) so a provider key stays
// in the server environment; these defaults only apply if that request fails.
export interface TileConfig {
  tileUrl: string;
  attribution: string;
  maxZoom: number;
}

const DEFAULT_TILES: TileConfig = {
  tileUrl: "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
  attribution:
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  maxZoom: 19,
};

async function fetchTileConfig(): Promise<TileConfig> {
  try {
    const response = await fetch(`${apiBaseUrl()}/map/config`, { headers: authHeaders() });
    if (!response.ok) return DEFAULT_TILES;
    return (await response.json()) as TileConfig;
  } catch {
    return DEFAULT_TILES;
  }
}

/**
 * Opens the map dialog (src/map/map.html) for a bank's branches or for a
 * whole market (every institution's branches in a county / MSA / state / zip).
 *
 * The dialog runs in its own browser context and can't see our localStorage,
 * so the access token is handed over with messageChild once the page reports
 * ready (DialogApi 1.2). Office allows one dialog per add-in at a time, so an
 * open map is closed before another is shown.
 */

// What the dialog should show; the dialog's InitMessage adds apiUrl/token/tiles.
type MapTarget =
  | { mode: "bank"; bank: { cert: number; name: string }; year?: number }
  | { mode: "market"; market: { geo: string; name: string }; year?: number };

let mapDialog: Office.Dialog | null = null;

// The market map's "Open in task pane" button (dialog → messageParent
// "openBank"). TaskPane registers this so the bank opens on the Info page
// while the map stays open.
type OpenBankHandler = (bank: { cert: number; name: string | null }) => void;
let openBankHandler: OpenBankHandler | null = null;

export function setMapOpenBankHandler(handler: OpenBankHandler | null): void {
  openBankHandler = handler;
}

function closeMapDialog(): void {
  if (mapDialog) {
    try {
      mapDialog.close();
    } catch {
      /* already closed */
    }
    mapDialog = null;
  }
}

export function canOpenMap(): boolean {
  return Office.context.requirements.isSetSupported("DialogApi", "1.2");
}

export function openBranchMap(bank: BankInfo, year?: number): Promise<void> {
  return openMap({ mode: "bank", bank: { cert: bank.cert, name: bank.name }, year });
}

export function openMarketMap(option: MarketOption, year?: number): Promise<void> {
  return openMap({
    mode: "market",
    market: { geo: marketFormulaCode(option), name: option.name },
    year,
  });
}

function openMap(target: MapTarget): Promise<void> {
  const token = getToken();
  if (!token) return Promise.reject(new Error("Sign in to open the branch map."));
  if (!canOpenMap()) {
    return Promise.reject(
      new Error("This version of Excel can't open the branch map (Dialog API 1.2 required).")
    );
  }

  closeMapDialog();
  // The query string is only a hint for debugging; everything the page needs
  // arrives in the init message, and the token never goes in the URL.
  const hint =
    target.mode === "bank"
      ? `cert=${target.bank.cert}`
      : `market=${encodeURIComponent(target.market.geo)}`;
  const url = `${addinOrigin()}/map.html?${hint}`;

  // Fetched while the dialog opens; awaited before the init message is sent.
  const tilesPromise = fetchTileConfig();

  return new Promise<void>((resolve, reject) => {
    Office.context.ui.displayDialogAsync(
      url,
      { height: 80, width: 80, promptBeforeOpen: false },
      (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          reject(new Error(asyncResult.error.message));
          return;
        }
        mapDialog = asyncResult.value;

        mapDialog.addEventHandler(Office.EventType.DialogMessageReceived, async (arg) => {
          const { message } = arg as { message: string };
          let data: { type?: string; cert?: number; name?: string | null } = {};
          try {
            data = JSON.parse(message) as typeof data;
          } catch {
            return;
          }
          if (data.type === "openBank" && typeof data.cert === "number") {
            openBankHandler?.({ cert: data.cert, name: data.name ?? null });
            return;
          }
          if (data.type === "ready") {
            const tiles = await tilesPromise;
            if (!mapDialog) return; // closed while the config was loading
            mapDialog.messageChild(
              JSON.stringify({
                type: "init",
                ...target,
                apiUrl: apiBaseUrl(),
                token: getToken(),
                tiles,
              })
            );
            resolve();
          }
        });

        // 12006 = the user closed the dialog.
        mapDialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
          mapDialog = null;
        });
      }
    );
  });
}
